import React, { useState, useRef, useEffect } from "react";
import {
  X,
  FileText,
  FileImage,
  FileAudio,
  FileVideo,
  File,
  FileSpreadsheet,
  Plus,
} from "lucide-react";
import OptimizedPDFViewer from "./OptimizedPDFViewer";
import { useTranslation } from "../hooks/useTranslation";
import { useAuth } from "../context/ApiKeyContext";
import { getApiBaseUrl } from "../config";

/**
 * Função auxiliar para obter o ícone apropriado baseado no tipo do arquivo
 */
const getFileIcon = (fileType, size = 16) => {
  const iconProps = { size, style: { flexShrink: 0 } };

  switch (fileType) {
    case "PDF":
      return <FileText {...iconProps} />;
    case "IMAGE":
      return <FileImage {...iconProps} />;
    case "AUDIO":
      return <FileAudio {...iconProps} />;
    case "VIDEO":
      return <FileVideo {...iconProps} />;
    case "XLSX":
      return <FileSpreadsheet {...iconProps} />;
    case "DOCX":
    case "TEXT":
      return <File {...iconProps} />;
    default:
      return <File {...iconProps} />;
  }
};

/**
 * Componente da barra lateral que exibe as fontes/origens dos itens do documento
 * e permite visualizar os documentos de origem
 */
const DocumentSourcesSidebar = ({
  sources, // { origem_dados, origem_escrita, origem_usuario (teses/subsídios) }
  allDocuments,
  originDocuments = [],
  onClose,
  initialSourcesHeight = 20, // Default 20% como o usuário testou e ficou bom
  loadingReferences = false, // Indica se está carregando as referências
  jobId = null, // ID do job (necessário para buscar referências de página)
  documentId = null, // ID do documento
  itemId = null, // ID do item atual
  currentItemType = null, // Tipo do item atual ('text' ou 'image')
  onItemUpdated = null, // Callback para notificar atualização do item (token, coordenadas, etc.)
  initialDocumentOrigemId = null, // ID do documento de origem para inicializar (se item já tem referência salva)
  initialPageNumber = null, // Número da página para inicializar (se item já tem referência salva)
  initialCoordenadas = null, // Coordenadas da área de imagem para inicializar (se item já tem referência salva)
  onOriginDocumentsUpdated = null, // Callback para notificar quando a lista de documentos de origem precisa ser atualizada
  editavel = true, // Define se o documento é editável (desabilita upload de PDF e edição de área de imagem)
}) => {
  const { t } = useTranslation("editor");
  const { token } = useAuth();
  const API_BASE_URL = getApiBaseUrl();

  // Log para debug: mostrar valores iniciais recebidos
  React.useEffect(() => {
    if (initialDocumentOrigemId || initialPageNumber) {
      console.log("🔍 DocumentSourcesSidebar inicializado com:", {
        initialDocumentOrigemId,
        initialPageNumber,
        itemId,
        currentItemType,
      });
    }
  }, [initialDocumentOrigemId, initialPageNumber, itemId, currentItemType]);

  // Combinar origem_dados, origem_escrita e origem_usuario (teses/subsídios) numa lista com tipo
  const allSources = React.useMemo(() => {
    if (!sources) return [];

    const origemDados = (sources.origem_dados || []).map((s) => ({
      ...s,
      tipo_origem: "dados",
      // id_item_referencia distingue dois trechos no mesmo doc+página
      uniqueKey: `${s.id}_${s.pagina}_${s.id_item_referencia ?? ""}`,
    }));
    const origemEscrita = (sources.origem_escrita || []).map((s) => ({
      ...s,
      tipo_origem: "escrita",
      uniqueKey: `${s.id}_${s.pagina}_${s.id_item_referencia ?? ""}`,
    }));
    const origemUsuario = (sources.origem_usuario || []).map((s) => ({
      ...s,
      tipo_origem: "usuario",
      uniqueKey: `${s.id}_${s.pagina}_${s.id_item_referencia ?? ""}`,
    }));

    const combined = [...origemDados, ...origemEscrita, ...origemUsuario];

    // Log para verificar as chaves únicas
    console.log("✓ Referências carregadas com chaves únicas (id + página):", {
      total: combined.length,
      referencias: combined.map((s) => ({
        uniqueKey: s.uniqueKey,
        id: s.id,
        nome: s.nome_arquivo,
        pagina: s.pagina,
      })),
    });

    return combined;
  }, [sources]);

  const [selectedSourceId, setSelectedSourceId] = useState(null);

  // Inicializar com documento salvo no item ou o primeiro da lista
  const getInitialDocumentId = () => {
    if (initialDocumentOrigemId) {
      // Verificar se o documento existe na lista
      const docExists = originDocuments.some(
        (doc) => doc.id === initialDocumentOrigemId,
      );
      if (docExists) {
        console.log(
          "📄 Inicializando com documento salvo no item:",
          initialDocumentOrigemId,
        );
        return initialDocumentOrigemId;
      }
    }
    return originDocuments && originDocuments.length > 0
      ? originDocuments[0].id
      : null;
  };

  const [selectedOriginDocId, setSelectedOriginDocId] = useState(
    getInitialDocumentId(),
  );
  const [viewerDocumentId, setViewerDocumentId] = useState(
    getInitialDocumentId(),
  );

  const [sourcesHeight, setSourcesHeight] = useState(initialSourcesHeight);
  const [pageReferences, setPageReferences] = useState(null); // Armazena as referências de página

  // Estado sincronizado para garantir que página e objetos sejam atualizados juntos
  // Inicializar com página salva no item ou página 1
  const [pdfViewerState, setPdfViewerState] = useState({
    pageNumber: initialPageNumber || 1,
    objects: [],
  });

  // Estado para mapear referência selecionada → página (para enviar objetos apenas na página correta)
  const [selectedReferencePage, setSelectedReferencePage] = useState(null);

  // Estado para controlar recriação do PDF Viewer - só atualiza quando temos dados completos da API
  const [pdfViewerKey, setPdfViewerKey] = useState(0);

  // Estado para controlar o retângulo de seleção de imagem
  const [imageSelectionBox, setImageSelectionBox] = useState(null);

  // Estado para indicar se o item atual é do tipo imagem
  // Este estado é baseado no tipo do item principal do documento (não apenas das referências)
  const [currentItemIsImage, setCurrentItemIsImage] = useState(false);

  // Estado para controlar o popup de confirmação ao descartar mudanças
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [pendingPageChange, setPendingPageChange] = useState(null);

  // Estados para upload de PDF
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const fileInputRef = useRef(null);

  // Refs
  const sideResizeRef = useRef(null);
  const pdfViewerRef = useRef(null);
  const currentPageRef = useRef(1); // Rastrear página atual sem causar re-renders

  // Sincronizar currentItemIsImage com o prop currentItemType
  // Isso garante que o botão "Criar Área" apareça sempre que o item é do tipo imagem,
  // mesmo quando o usuário muda de documento no combo e não há referência selecionada
  useEffect(() => {
    const isImage = currentItemType === "image";
    console.log("📸 Atualizando currentItemIsImage baseado no tipo do item:", {
      currentItemType,
      isImage,
    });
    setCurrentItemIsImage(isImage);
  }, [currentItemType]);

  // Carregar coordenadas iniciais quando o componente monta (se item já tem referência salva)
  useEffect(() => {
    if (initialCoordenadas && initialPageNumber && pdfViewerRef.current) {
      console.log("📐 Carregando coordenadas iniciais salvas no item:", {
        initialCoordenadas,
        initialPageNumber,
      });

      // Aguardar um momento para garantir que o PDF Viewer está montado
      setTimeout(() => {
        if (pdfViewerRef.current && pdfViewerRef.current.setSelectionBox) {
          pdfViewerRef.current.setSelectionBox({
            x1: initialCoordenadas.x1,
            y1: initialCoordenadas.y1,
            x2: initialCoordenadas.x2,
            y2: initialCoordenadas.y2,
            page: initialPageNumber,
          });
          console.log("✅ Coordenadas iniciais carregadas no PDF Viewer");
        }
      }, 500);
    }
  }, [initialCoordenadas, initialPageNumber]);

  // Log para debug quando selectedSourceId mudar
  useEffect(() => {
    console.log("Estado selectedSourceId atualizado:", selectedSourceId);
  }, [selectedSourceId]);

  // Atualizar a fonte selecionada quando as fontes mudarem
  useEffect(() => {
    if (allSources && allSources.length > 0) {
      // Se a fonte atual não está mais na lista, selecionar automaticamente a primeira referência
      const currentSourceExists = allSources.find(
        (s) => s.uniqueKey === selectedSourceId,
      );
      if (!currentSourceExists) {
        console.log(
          "📌 Selecionando automaticamente a primeira referência:",
          allSources[0].uniqueKey,
        );
        setSelectedSourceId(allSources[0].uniqueKey);
      }
    } else {
      // Não há fontes disponíveis
      if (selectedSourceId !== null) {
        console.log("🔄 Limpando seleção: não há referências disponíveis");
        setSelectedSourceId(null);
      }

      // Selecionar o primeiro documento do combo e ir para página 1
      if (originDocuments && originDocuments.length > 0) {
        const firstDocId = originDocuments[0].id;
        console.log(
          "📄 Sem referências: selecionando primeiro documento do combo:",
          firstDocId,
        );

        // Atualizar documento selecionado
        setSelectedOriginDocId(firstDocId);
        setViewerDocumentId(firstDocId);

        // Ir para página 1
        setPdfViewerState({
          pageNumber: 1,
          objects: [],
        });

        // Forçar recriação do PDF Viewer
        setPdfViewerKey((prev) => prev + 1);

        console.log(
          "✅ Primeiro documento selecionado e visualizador na página 1",
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSources]);

  // Atualizar documento de origem selecionado quando a lista mudar
  useEffect(() => {
    if (originDocuments && originDocuments.length > 0) {
      // Se o documento atual não está mais na lista, selecionar o primeiro
      const currentDocExists = originDocuments.find(
        (d) => d.id === selectedOriginDocId,
      );
      if (!currentDocExists) {
        console.log(
          "🔄 Selecionando primeiro documento: documento atual não existe mais na lista",
        );
        setSelectedOriginDocId(originDocuments[0].id);
        setViewerDocumentId(originDocuments[0].id);
      }
    } else {
      // Só limpar se havia algo selecionado
      if (selectedOriginDocId !== null) {
        console.log(
          "🔄 Limpando documento selecionado: não há documentos disponíveis",
        );
        setSelectedOriginDocId(null);
        setViewerDocumentId(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originDocuments]);

  // Buscar referências de página quando uma referência é selecionada
  useEffect(() => {
    // Não fazer nada se não há referência selecionada
    if (!selectedSourceId) {
      // Apenas limpar o estado se houver referências de página armazenadas
      if (pageReferences !== null) {
        console.log(
          "🔄 Limpando referências de página: nenhuma referência selecionada",
        );
        setPageReferences(null);
      }
      return;
    }

    console.log(
      "🔍 Buscando referências da página para referência selecionada:",
      {
        selectedSourceId,
        jobId,
        documentId,
        itemId,
        allSourcesLength: allSources?.length,
        hasToken: !!token,
      },
    );

    const fetchPageReferences = async () => {
      if (!jobId || !documentId || !itemId) {
        console.warn("⚠️ Informações necessárias faltando:", {
          jobId,
          documentId,
          itemId,
        });
        return;
      }

      // Buscar a referência selecionada usando a uniqueKey
      const selectedSource = allSources?.find(
        (s) => s.uniqueKey === selectedSourceId,
      );
      if (!selectedSource) {
        console.warn(
          "⚠️ Referência selecionada não encontrada na lista:",
          selectedSourceId,
        );
        return;
      }

      if (!selectedSource.pagina) {
        console.warn("⚠️ Referência sem número de página:", selectedSource);
        return;
      }

      try {
        // Usar o id original do documento (não a uniqueKey) para a chamada da API
        const url = `${API_BASE_URL}/doc_creator/review/page-references/${jobId}/${documentId}/${itemId}/${selectedSource.id}/${selectedSource.pagina}`;
        console.log("📡 Buscando referências da página:", {
          url,
          uniqueKey: selectedSource.uniqueKey,
          documentOrigemId: selectedSource.id,
          pagina: selectedSource.pagina,
        });

        const response = await fetch(url, {
          method: "GET",
          headers: {
            accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(
            `Erro ao buscar referências da página: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        console.log("✅ Referências da página recebidas:", {
          data,
          totalReferencias: data.referencias?.length || 0,
          referencias: data.referencias,
          paraReferencia: selectedSourceId,
        });

        // Adicionar metadados para validar depois
        setPageReferences({
          ...data,
          _referenceId: selectedSourceId, // Marcar de qual referência esses dados vieram
        });
      } catch (error) {
        console.error("❌ Erro ao buscar referências da página:", error);
        setPageReferences(null);
      }
    };

    fetchPageReferences();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedSourceId,
    jobId,
    documentId,
    itemId,
    allSources,
    API_BASE_URL,
    token,
  ]);
  // Nota: pageReferences não está nas dependências intencionalmente para evitar loop infinito
  // O useEffect modifica pageReferences, então adicioná-lo causaria re-execução contínua

  // Processar resposta da API de referências de página
  useEffect(() => {
    // Só processar se houver referências de página E uma referência selecionada
    if (!pageReferences || !selectedSourceId) {
      return;
    }

    // IMPORTANTE: Validar se pageReferences corresponde ao selectedSourceId atual
    // Isso evita race condition quando clicamos rapidamente em várias referências
    if (pageReferences._referenceId !== selectedSourceId) {
      console.warn("⚠️ Ignorando pageReferences desatualizado:", {
        pageReferencesReferenceId: pageReferences._referenceId,
        selectedSourceId: selectedSourceId,
      });
      return;
    }

    console.log(
      "✅ Processando referências da página (validado):",
      pageReferences,
    );

    // Buscar a referência selecionada
    const selectedSource = allSources?.find(
      (s) => s.uniqueKey === selectedSourceId,
    );
    if (!selectedSource) {
      console.warn("Referência selecionada não encontrada");
      return;
    }

    // Atualizar o viewerDocumentId E o combo (selectedOriginDocId) para que mostre o documento correto
    if (selectedSource.id !== viewerDocumentId) {
      console.log(
        "🔄 Atualizando documento do viewer e combo de",
        viewerDocumentId,
        "para",
        selectedSource.id,
        "(mudança programática)",
      );

      setSelectedOriginDocId(selectedSource.id);
      setViewerDocumentId(selectedSource.id);
    }

    // Armazenar a página da referência selecionada
    setSelectedReferencePage(selectedSource.pagina);
    currentPageRef.current = selectedSource.pagina; // Atualizar ref também

    // Preparar os objetos de highlight
    const newObjects =
      pageReferences.referencias && Array.isArray(pageReferences.referencias)
        ? pageReferences.referencias
        : [];

    if (newObjects.length > 0) {
      console.log("🎨 Objetos de highlight recebidos:", {
        total: newObjects.length,
        referencias: newObjects,
        exemplo: newObjects[0],
      });
    } else {
      console.log("⚠️ Sem referências de highlight na resposta");
    }

    // NOVO: Detectar e processar referências de imagem com coordenadas
    const imageReference = newObjects.find(
      (ref) =>
        ref.tipo_item === "IMAGEM" &&
        ref.coordenadas &&
        ref.coordenadas.x1 !== undefined &&
        ref.coordenadas.y1 !== undefined &&
        ref.coordenadas.x2 !== undefined &&
        ref.coordenadas.y2 !== undefined,
    );

    // NOTA: currentItemIsImage agora é definido pelo prop currentItemType (via useEffect acima)
    // não mais pelas referências da API. Isso garante que o botão "Criar Área" persista
    // mesmo quando o usuário muda de documento no combo.

    if (imageReference) {
      console.log("📐 Referência de imagem com coordenadas detectada:", {
        id: imageReference.id,
        tipo_item: imageReference.tipo_item,
        coordenadas: imageReference.coordenadas,
        pagina: selectedSource.pagina,
      });

      // Criar objeto de seleção box
      const selectionBox = {
        x1: imageReference.coordenadas.x1,
        y1: imageReference.coordenadas.y1,
        x2: imageReference.coordenadas.x2,
        y2: imageReference.coordenadas.y2,
        page: selectedSource.pagina,
      };

      console.log("🎯 Preparando retângulo de seleção:", selectionBox);

      // Armazenar no estado
      setImageSelectionBox(selectionBox);
    } else {
      // Limpar retângulo se não houver referência de imagem COM COORDENADAS
      if (imageSelectionBox) {
        console.log(
          "🧹 Limpando retângulo: sem referência de imagem com coordenadas",
        );
        setImageSelectionBox(null);
      }
    }

    // IMPORTANTE: Limpar cache do PDF Viewer quando uma nova referência é selecionada
    // Isso garante que mesmo se for o mesmo documento+página, os novos objetos serão buscados
    if (pdfViewerRef.current && pdfViewerRef.current.clearCache) {
      console.log("🧹 Limpando cache do PDF Viewer para nova referência");
      pdfViewerRef.current.clearCache();
    }

    // IMPORTANTE: Atualizar estado sincronizado de uma vez só
    // Isso garante que OptimizedPDFViewer receba página E objetos juntos
    console.log("📦 Atualizando estado sincronizado do PDF Viewer:", {
      pageNumber: selectedSource.pagina,
      objectsCount: newObjects.length,
      documentoId: selectedSource.id,
      documentoViewer: viewerDocumentId,
      documentoCombo: selectedOriginDocId,
      mesmoDocumento: selectedSource.id === viewerDocumentId,
    });

    setPdfViewerState({
      pageNumber: selectedSource.pagina,
      objects: newObjects,
    });

    // CRUCIAL: Atualizar a chave do PDF Viewer APÓS processar todos os dados
    // Isso força a recriação do componente apenas quando temos documento + página + objetos prontos
    console.log(
      "🔑 Incrementando chave do PDF Viewer para forçar recriação com dados completos",
    );
    setPdfViewerKey((prev) => prev + 1);

    // Aplicar retângulo de seleção se houver (após PDF Viewer ser recriado)
    // IMPORTANTE: Usar imageReference diretamente (não imageSelectionBox) pois o estado ainda não foi atualizado
    if (imageReference) {
      const boxToApply = {
        x1: imageReference.coordenadas.x1,
        y1: imageReference.coordenadas.y1,
        x2: imageReference.coordenadas.x2,
        y2: imageReference.coordenadas.y2,
        page: selectedSource.pagina,
      };

      setTimeout(() => {
        if (pdfViewerRef.current && pdfViewerRef.current.setSelectionBox) {
          console.log(
            "✅ Aplicando retângulo de seleção ao PDF Viewer:",
            boxToApply,
          );
          pdfViewerRef.current.setSelectionBox(boxToApply);
        } else {
          console.warn(
            "⚠️ PDF Viewer ref não disponível para aplicar retângulo",
          );
        }
      }, 300); // Delay maior para garantir que o PDF Viewer foi recriado
    }

    // NOTA: Não é necessário chamar goToPage() manualmente
    // A atualização de pdfViewerState já dispara a sincronização via initialPageNumber
    // Chamar goToPage() causaria uma chamada duplicada à API
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageReferences, allSources, selectedSourceId]);
  // Notas sobre dependências:
  // - viewerDocumentId: Não está nas dependências porque o useEffect o modifica (linha 280), causaria loop
  // - selectedOriginDocId: Não está nas dependências porque o useEffect o modifica (linha 277), causaria loop
  // - Queremos reagir apenas a: pageReferences (resposta da API), allSources e selectedSourceId

  // Limpar highlights quando não há referência selecionada
  useEffect(() => {
    if (!selectedSourceId) {
      // Limpar a página de referência
      setSelectedReferencePage(null);

      // NOTA: Não limpar currentItemIsImage aqui, pois ele é baseado no tipo do item principal
      // (definido pelo prop currentItemType) e deve persistir mesmo sem referência selecionada

      // Limpar retângulo de seleção de imagem
      setImageSelectionBox(null);
      if (pdfViewerRef.current && pdfViewerRef.current.clearSelectionBox) {
        pdfViewerRef.current.clearSelectionBox();
      }

      // Só atualizar o estado se realmente houver objetos para limpar
      // Evita re-renders desnecessários quando já está limpo
      setPdfViewerState((prevState) => {
        if (prevState.objects.length > 0 || prevState.pageNumber !== 1) {
          console.log("🧹 Limpando highlights: nenhuma referência selecionada");

          // CRUCIAL: Incrementar chave para forçar recriação sem objetos
          console.log(
            "🔑 Incrementando chave do PDF Viewer ao limpar referência",
          );
          setPdfViewerKey((prev) => prev + 1);

          return {
            pageNumber: 1,
            objects: [],
          };
        }
        return prevState; // Mantém o estado atual se já está limpo
      });
    }
  }, [selectedSourceId]);

  // Função auxiliar para limpar retângulo e navegar para nova página
  const clearSelectionBoxAndNavigate = React.useCallback(
    (newPage) => {
      console.log("🧹 Limpando retângulo e navegando para página", newPage);

      // Atualizar ref de página
      currentPageRef.current = newPage;

      // Navegar para a nova página no viewer
      if (pdfViewerRef.current && pdfViewerRef.current.goToPage) {
        pdfViewerRef.current.goToPage(newPage);
      }

      // Limpar retângulo
      if (pdfViewerRef.current && pdfViewerRef.current.clearSelectionBox) {
        pdfViewerRef.current.clearSelectionBox();
      }

      // Limpar objetos também se houver
      if (pdfViewerState.objects.length > 0) {
        setPdfViewerState({
          pageNumber: newPage,
          objects: [],
        });
      }

      // Fechar popup de confirmação se estiver aberto
      setShowDiscardConfirm(false);
      setPendingPageChange(null);
    },
    [pdfViewerState.objects.length],
  );

  // Handler para quando o PDF Viewer muda de página (via navegação manual do usuário)
  // NOTA: Agora a validação de mudanças não salvas acontece ANTES em handleRequestPageChange
  // Este handler apenas sincroniza o estado e restaura objetos quando necessário
  const handlePdfPageChange = React.useCallback(
    (newPage) => {
      console.log("📄 PDF Viewer mudou de página para:", newPage);

      // Atualizar ref de página
      currentPageRef.current = newPage;

      // Se há referência selecionada, usar lógica de restaurar/limpar baseada na página da referência
      if (selectedReferencePage) {
        // Se está na página da referência selecionada, restaurar objetos E retângulo de imagem
        if (newPage === selectedReferencePage) {
          console.log(
            "✨ Voltou para página da referência, restaurando highlights",
          );
          setPdfViewerState({
            pageNumber: newPage,
            objects: pageReferences?.referencias || [],
          });

          // Restaurar retângulo de seleção se houver
          if (
            imageSelectionBox &&
            pdfViewerRef.current &&
            pdfViewerRef.current.setSelectionBox
          ) {
            console.log("🎯 Restaurando retângulo de seleção na página");
            pdfViewerRef.current.setSelectionBox(imageSelectionBox);
          }
        }
        // Se mudou para outra página, limpar objetos (mas manter a referência selecionada)
        else if (pdfViewerState.objects.length > 0) {
          console.log("🧹 Mudou para página diferente, limpando highlights");
          setPdfViewerState({
            pageNumber: newPage,
            objects: [],
          });

          // Limpar retângulo de seleção também
          if (pdfViewerRef.current && pdfViewerRef.current.clearSelectionBox) {
            pdfViewerRef.current.clearSelectionBox();
          }
        }
      }
      // Se NÃO há referência selecionada, apenas limpar retângulo
      // (a validação de mudanças já foi feita em handleRequestPageChange)
      else {
        console.log("🧹 Sem referência selecionada, limpando retângulo");
        if (pdfViewerRef.current && pdfViewerRef.current.clearSelectionBox) {
          pdfViewerRef.current.clearSelectionBox();
        }

        // Limpar objetos também se houver
        if (pdfViewerState.objects.length > 0) {
          setPdfViewerState({
            pageNumber: newPage,
            objects: [],
          });
        }
      }
    },
    [
      selectedReferencePage,
      pdfViewerState.objects.length,
      pageReferences,
      imageSelectionBox,
    ],
  );

  // Callback para quando o OptimizedPDFViewer solicita mudança de página
  const handleRequestPageChange = React.useCallback(
    async (newPage) => {
      console.log(
        "🔔 OptimizedPDFViewer solicitou mudança de página para:",
        newPage,
      );

      // Se há referência selecionada, permitir navegação livre
      if (selectedReferencePage) {
        console.log("✅ Há referência selecionada, permitindo navegação");
        return true;
      }

      // Se NÃO há referência selecionada, verificar se há mudanças não salvas
      const hasUnsavedChanges =
        pdfViewerRef.current &&
        pdfViewerRef.current.getHasChanges &&
        pdfViewerRef.current.getHasChanges();

      if (hasUnsavedChanges) {
        console.log(
          "⚠️ Há mudanças não salvas, bloqueando navegação e mostrando popup",
        );
        setPendingPageChange(newPage);
        setShowDiscardConfirm(true);
        return false; // Bloquear a navegação
      }

      // Sem mudanças não salvas, permitir navegação
      console.log("✅ Sem mudanças não salvas, permitindo navegação");
      return true;
    },
    [selectedReferencePage],
  );

  // Handler para quando a referência de imagem é atualizada com sucesso
  const handleImageReferenceUpdated = React.useCallback(
    (apiResult) => {
      console.log("🎨 Referência de imagem atualizada:", apiResult);

      // Propagar atualização para o componente pai (DocumentEditor)
      // O DocumentEditor irá recarregar as referências automaticamente
      if (onItemUpdated) {
        onItemUpdated({
          itemId: apiResult.item_id,
          token: apiResult.token,
          coordenadas: apiResult.coordenadas,
          pagina: apiResult.pagina,
          documentoOrigemId: apiResult.documento_origem_id,
          dataAprovacao: apiResult.data_aprovacao,
          aprovadoPor: apiResult.aprovado_por,
        });
      }
    },
    [onItemUpdated],
  );

  // Nota: O handler acima será passado para o OptimizedPDFViewer
  // Por enquanto, vamos usar polling simplificado apenas para sincronizar o estado
  // TODO: Modificar OptimizedPDFViewer para aceitar onPageChange callback
  useEffect(() => {
    if (!pdfViewerRef.current || !selectedReferencePage) {
      return;
    }

    const checkPageChange = setInterval(() => {
      if (pdfViewerRef.current && pdfViewerRef.current.getCurrentPage) {
        const currentPage = pdfViewerRef.current.getCurrentPage();

        // Apenas processar se a página mudou de fato
        if (currentPage !== currentPageRef.current) {
          handlePdfPageChange(currentPage);
        }
      }
    }, 500);

    return () => clearInterval(checkPageChange);
  }, [selectedReferencePage, handlePdfPageChange]);

  // Função para lidar com upload de PDF
  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validar tipo de arquivo
    if (file.type !== "application/pdf") {
      setUploadError("Por favor, selecione um arquivo PDF válido.");
      return;
    }

    // Validar tamanho do arquivo (50MB max)
    const maxSize = 50 * 1024 * 1024; // 50MB
    if (file.size > maxSize) {
      setUploadError("O arquivo é muito grande. O tamanho máximo é 50MB.");
      return;
    }

    if (!jobId) {
      setUploadError("ID do job não disponível.");
      return;
    }

    try {
      setUploadingPdf(true);
      setUploadError(null);

      console.log("📤 Iniciando upload do PDF:", {
        nome: file.name,
        tamanho: file.size,
        jobId,
      });

      // Criar FormData
      const formData = new FormData();
      formData.append("arquivo_pdf", file);

      // Fazer upload
      const url = `${API_BASE_URL}/doc_creator/review/add-origin-document/${jobId}`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.detail ||
            `Erro ao fazer upload: ${response.status} ${response.statusText}`,
        );
      }

      const result = await response.json();
      console.log("✅ Upload concluído com sucesso:", result);

      // Notificar componente pai para recarregar a lista de documentos de origem
      if (onOriginDocumentsUpdated) {
        const updatedDocuments = await onOriginDocumentsUpdated();

        // Após recarregar, encontrar o novo documento e selecioná-lo
        if (updatedDocuments && updatedDocuments.length > 0) {
          // O novo documento deve ser o último da lista (ou encontrar pelo nome)
          const newDoc = updatedDocuments.find(
            (doc) => doc.nome_arquivo === result.arquivo,
          );

          if (newDoc) {
            console.log("📄 Selecionando novo documento:", newDoc);

            // Selecionar o novo documento
            setSelectedOriginDocId(newDoc.id);
            setViewerDocumentId(newDoc.id);

            // Limpar referência selecionada
            setSelectedSourceId(null);
            setSelectedReferencePage(null);

            // Ir para página 1
            setPdfViewerState({
              pageNumber: 1,
              objects: [],
            });

            // Forçar recriação do PDF Viewer
            setPdfViewerKey((prev) => prev + 1);

            console.log(
              "✅ Novo documento selecionado e visualizador atualizado",
            );
          }
        }
      }

      // Limpar input de arquivo
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      console.error("❌ Erro ao fazer upload do PDF:", error);
      setUploadError(error.message || "Erro ao fazer upload do arquivo.");
    } finally {
      setUploadingPdf(false);
    }
  };

  // Função para abrir seletor de arquivo
  const handleAddPdfClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Obter documento selecionado (para exibição no combo)
  const selectedOriginDoc = originDocuments?.find(
    (d) => d.id === selectedOriginDocId,
  );

  // Obter documento que o viewer deve exibir (pode ser diferente do combo)
  const viewerDocument = originDocuments?.find(
    (d) => d.id === viewerDocumentId,
  );

  useEffect(() => {
    const handleSideResize = (e) => {
      if (sideResizeRef.current) {
        const sidebar = window.document.getElementById(
          "document-sources-sidebar",
        );
        if (sidebar) {
          const rect = sidebar.getBoundingClientRect();
          const newHeight = ((e.clientY - rect.top) / rect.height) * 100;
          if (newHeight > 20 && newHeight < 80) {
            setSourcesHeight(newHeight);
          }
        }
      }
    };

    const handleMouseUp = () => {
      sideResizeRef.current = false;
    };

    window.document.addEventListener("mousemove", handleSideResize);
    window.document.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.document.removeEventListener("mousemove", handleSideResize);
      window.document.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const selectedSource = allSources?.find(
    (s) => s.uniqueKey === selectedSourceId,
  );

  // Ajustar altura da seção de fontes se não houver fontes
  const effectiveSourcesHeight =
    allSources && allSources.length > 0 ? sourcesHeight : 0;

  // Função para obter cor baseada no tipo de origem
  const getOriginTypeColor = (tipoOrigem) => {
    if (tipoOrigem === "dados") {
      return {
        bg: "var(--ikaro-source-data-bg, #E0F2FE)",
        text: "var(--ikaro-source-data-text, #0369A1)",
        border: "var(--ikaro-source-data-border, #0EA5E9)",
      };
    } else if (tipoOrigem === "escrita") {
      return {
        bg: "var(--ikaro-source-writing-bg, #F3E8FF)",
        text: "var(--ikaro-source-writing-text, #7C3AED)",
        border: "var(--ikaro-source-writing-border, #A78BFA)",
      };
    } else if (tipoOrigem === "usuario") {
      return {
        bg: "var(--ikaro-source-user-bg, #FFFBEB)",
        text: "var(--ikaro-source-user-text, #B45309)",
        border: "var(--ikaro-source-user-border, #F59E0B)",
      };
    }
    return {
      bg: "var(--ikaro-gray-light)",
      text: "var(--ikaro-gray-darker)",
      border: "var(--ikaro-gray-medium)",
    };
  };

  // Função para obter texto do tipo de origem
  const getOriginTypeText = (tipoOrigem) => {
    if (tipoOrigem === "dados") {
      return "Origem de Dados";
    } else if (tipoOrigem === "escrita") {
      return "Origem de Escrita";
    } else if (tipoOrigem === "usuario") {
      return "Tese / subsídio";
    }
    return "Origem";
  };

  // Função para obter o rótulo traduzido
  const getDocumentLabel = (rotulo) => {
    const labelKey = `documentLabels.${rotulo}`;
    const translatedLabel = t(labelKey);
    // Se a tradução retornar a chave, significa que não foi encontrada, então retorna o rotulo original
    return translatedLabel === labelKey ? rotulo : translatedLabel;
  };

  return (
    <div id="document-sources-sidebar" className="source-sidebar">
      {/* Seção de lista de fontes - só mostrar se houver fontes */}
      {allSources && allSources.length > 0 && (
        <>
          <div
            className="flex-col overflow-hidden"
            style={{ height: `${effectiveSourcesHeight}%` }}
          >
            {/* Header da seção de fontes */}
            <div className="source-sidebar-refs-header">
              <div className="flex-row-between">
                <div className="flex-row gap-8">
                  <h2 className="source-sidebar-refs-title">
                    Referências do Item
                  </h2>
                  <span className="source-badge-purple">
                    {allSources?.length || 0}
                  </span>
                </div>
                <button
                  onClick={onClose}
                  className="ikaro-btn ikaro-btn-ghost"
                  title="Fechar painel"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Lista de fontes com scroll */}
            <div className="source-sidebar-scroll">
              {loadingReferences ? (
                <div className="source-sidebar-loading">
                  <div className="source-sidebar-spinner" />
                  <p className="text-muted text-sm">
                    Carregando referências...
                  </p>
                </div>
              ) : allSources.length === 0 ? (
                <div className="ikaro-empty-state">
                  <FileText size={32} />
                  <p className="text-sm">
                    Nenhuma referência disponível para este item.
                  </p>
                </div>
              ) : (
                allSources.map((source) => {
                  const colors = getOriginTypeColor(source.tipo_origem);
                  const isSelected = source.uniqueKey === selectedSourceId;

                  return (
                    <div
                      key={source.uniqueKey}
                      onClick={(e) => {
                        e.stopPropagation();
                        console.log("👆 Clique em referência:", {
                          uniqueKey: source.uniqueKey,
                          id: source.id,
                          nome: source.nome_arquivo,
                          pagina: source.pagina,
                          tipo_origem: source.tipo_origem,
                          jobId,
                          documentId,
                          itemId,
                        });
                        setSelectedSourceId(source.uniqueKey);
                      }}
                      className="source-item-row"
                      style={{
                        backgroundColor: isSelected
                          ? colors.bg
                          : "var(--ikaro-bg-surface)",
                        border: `1px solid ${isSelected ? colors.border : "var(--ikaro-border-default)"}`,
                        boxShadow: isSelected
                          ? "var(--ikaro-shadow-light)"
                          : "none",
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor =
                            "var(--ikaro-bg-surface-hover)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor =
                            "var(--ikaro-bg-surface)";
                        }
                      }}
                    >
                      {/* Ícone do tipo de arquivo */}
                      <div
                        className="flex-row flex-shrink-0"
                        style={{ color: colors.text }}
                      >
                        {getFileIcon(source.tipo_arquivo, 14)}
                      </div>

                      {/* Tipo de origem - discreto (apenas texto) */}
                      <div
                        className="source-origin-type-label"
                        style={{ color: colors.text }}
                        title={getOriginTypeText(source.tipo_origem)}
                      >
                        {source.tipo_origem === "dados"
                          ? "DADO"
                          : source.tipo_origem === "escrita"
                            ? "ESCRITA"
                            : source.tipo_origem === "usuario"
                              ? "TESE"
                              : "REF."}
                      </div>

                      {/* Nome do arquivo */}
                      <div
                        className="source-item-filename"
                        title={source.nome_arquivo}
                      >
                        {source.nome_arquivo}
                      </div>

                      {/* Rótulo - oculta em telas pequenas */}
                      <div
                        className="source-label-tag source-label-tag-base"
                        style={{
                          backgroundColor: colors.bg,
                          color: colors.text,
                          border: `1px solid ${colors.border}`,
                        }}
                      >
                        {getDocumentLabel(source.rotulo)}
                      </div>

                      {/* Página - sempre visível */}
                      <div
                        className="source-item-page"
                        style={{ color: colors.text }}
                      >
                        Pág. {source.pagina}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Divisor redimensionável - só mostrar se houver fontes */}
          <div
            className="source-sidebar-resize-handle"
            onMouseDown={() => {
              sideResizeRef.current = true;
            }}
            onMouseEnter={(e) =>
              (e.target.style.backgroundColor = "var(--ikaro-orange-primary)")
            }
            onMouseLeave={(e) => {
              if (!sideResizeRef.current) {
                e.target.style.backgroundColor = "var(--ikaro-gray-medium)";
              }
            }}
          />
        </>
      )}

      {/* Seção de visualização de documentos */}
      <div
        className="flex-col overflow-hidden"
        style={{ height: `${100 - effectiveSourcesHeight}%` }}
      >
        <div className="source-sidebar-viewer-header">
          <div className="flex-row-between mb-16">
            <h3 className="source-sidebar-refs-title">
              Visualizador de Documentos
            </h3>

            {/* Botão de fechar - só mostrar quando não há fontes ou quando em loading */}
            {(!allSources || allSources.length === 0 || loadingReferences) && (
              <button
                onClick={onClose}
                className="ikaro-btn ikaro-btn-ghost"
                title="Fechar painel"
              >
                <X size={18} />
              </button>
            )}
          </div>

          {/* Mensagem de erro do upload */}
          {uploadError && (
            <div className="source-sidebar-upload-error">
              <span className="source-sidebar-upload-error-text">
                {uploadError}
              </span>
              <button
                onClick={() => setUploadError(null)}
                className="source-sidebar-upload-error-btn"
                title="Fechar"
              >
                <X size={14} />
              </button>
            </div>
          )}

          <div className="flex-row gap-8">
            <label className="ikaro-label flex-shrink-0">Documento:</label>

            {/* Select com rótulo e nome do arquivo */}
            <div className="relative flex-1 min-w-0">
              <select
                value={selectedOriginDocId || ""}
                onChange={(e) => {
                  const newDocId = e.target.value;

                  console.log(
                    "👤 Usuário mudou documento manualmente:",
                    selectedOriginDocId,
                    "->",
                    newDocId,
                  );

                  // Atualizar documento selecionado no combo
                  setSelectedOriginDocId(newDocId);

                  // Atualizar documento do viewer também
                  setViewerDocumentId(newDocId);

                  // Limpar referência selecionada
                  if (selectedSourceId) {
                    console.log(
                      "🧹 Limpando referência selecionada devido a mudança manual de documento",
                    );
                    setSelectedSourceId(null);
                  }

                  // Limpar página de referência
                  setSelectedReferencePage(null);

                  // Resetar para página 1 e limpar objetos
                  setPdfViewerState({
                    pageNumber: 1,
                    objects: [],
                  });

                  // Forçar recriação do PDF Viewer
                  setPdfViewerKey((prev) => prev + 1);
                }}
                className="source-sidebar-select"
              >
                {originDocuments?.map((doc) => {
                  const label = getDocumentLabel(doc.rotulo);
                  const pages = doc.metadados?.total_paginas || 0;
                  const pagesText = pages === 1 ? "página" : "páginas";
                  return (
                    <option key={doc.id} value={doc.id}>
                      [{label}] {doc.nome_arquivo} ({pages} {pagesText})
                    </option>
                  );
                })}
              </select>

              {/* Ícone do tipo de arquivo */}
              {selectedOriginDoc && (
                <div className="source-sidebar-select-icon">
                  {getFileIcon(selectedOriginDoc.tipo_arquivo, 18)}
                </div>
              )}

              {/* Seta para baixo */}
              <div className="ikaro-select-arrow">
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M2 4L6 8L10 4"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>

            {/* Botão de adicionar PDF - escondido quando não é editável */}
            {jobId && editavel && (
              <button
                onClick={handleAddPdfClick}
                disabled={uploadingPdf}
                className="ikaro-btn ikaro-btn-primary source-sidebar-add-btn flex-shrink-0"
                title={uploadingPdf ? "Fazendo upload..." : "Adicionar PDF"}
              >
                {uploadingPdf ? (
                  <div className="source-sidebar-spinner-sm" />
                ) : (
                  <Plus size={18} />
                )}
              </button>
            )}

            {/* Input de arquivo (hidden) */}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>
        </div>

        {/* Área de visualização do documento */}
        <div className="source-sidebar-viewer-area">
          {viewerDocument &&
            viewerDocument.tipo_arquivo === "PDF" &&
            viewerDocument.token && (
              <div className="flex-1 overflow-hidden">
                <OptimizedPDFViewer
                  ref={pdfViewerRef}
                  key={`pdf-viewer-${pdfViewerKey}`}
                  token={viewerDocument.token}
                  initialPageNumber={pdfViewerState.pageNumber}
                  objects={pdfViewerState.objects}
                  width="100%"
                  height="100%"
                  showToolbar={true}
                  fileName={viewerDocument.nome_arquivo}
                  serviceName="DOCUMENT_CREATION"
                  onRequestPageChange={handleRequestPageChange}
                  currentItemIsImage={currentItemIsImage}
                  jobId={jobId}
                  documentId={documentId}
                  itemId={itemId}
                  documentoOrigemId={viewerDocumentId}
                  onImageReferenceUpdated={handleImageReferenceUpdated}
                  editavel={editavel}
                />
              </div>
            )}

          {viewerDocument && viewerDocument.tipo_arquivo !== "PDF" && (
            <div className="source-sidebar-empty-state">
              <div className="text-center text-muted">
                {getFileIcon(viewerDocument.tipo_arquivo, 48)}
                <p className="text-base font-medium mt-16 mb-8">
                  {viewerDocument.nome_arquivo}
                </p>
                <p className="text-xs">
                  Visualização de arquivos do tipo {viewerDocument.tipo_arquivo}{" "}
                  ainda não está disponível.
                </p>
              </div>
            </div>
          )}

          {!viewerDocument && originDocuments.length === 0 && (
            <div className="source-sidebar-empty-state">
              <div className="text-center text-muted">
                <FileText
                  size={48}
                  style={{ margin: "0 auto 16px", display: "block" }}
                />
                <p className="text-base font-medium mb-8">
                  Nenhum documento disponível
                </p>
                <p className="text-xs">
                  Não há documentos de origem para visualizar.
                </p>
              </div>
            </div>
          )}

          {/* Informações da região selecionada */}
          {selectedSource?.region && (
            <div className="source-sidebar-region-card">
              <p className="text-xs font-medium text-dark mb-8">
                Região selecionada na origem:
              </p>
              <div className="source-sidebar-region-grid">
                <div>
                  <span className="text-muted">X:</span>{" "}
                  <span className="text-dark">{selectedSource.region.x}px</span>
                </div>
                <div>
                  <span className="text-muted">Y:</span>{" "}
                  <span className="text-dark">{selectedSource.region.y}px</span>
                </div>
                <div>
                  <span className="text-muted">Largura:</span>{" "}
                  <span className="text-dark">
                    {selectedSource.region.width}px
                  </span>
                </div>
                <div>
                  <span className="text-muted">Altura:</span>{" "}
                  <span className="text-dark">
                    {selectedSource.region.height}px
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de confirmação para descartar mudanças */}
      {showDiscardConfirm && (
        <div
          className="source-sidebar-discard-overlay"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDiscardConfirm(false);
              setPendingPageChange(null);
            }
          }}
        >
          <div
            className="source-sidebar-discard-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold text-dark mb-8">
              Descartar alterações?
            </h3>
            <p className="text-base text-muted mb-20 line-height-1-5">
              Você fez alterações na área delimitada que não foram salvas. Ao
              trocar de página, essas alterações serão perdidas.
            </p>
            <div className="flex-row-end gap-12">
              <button
                onClick={() => {
                  setShowDiscardConfirm(false);
                  setPendingPageChange(null);
                }}
                className="ikaro-btn ikaro-btn-sm ikaro-btn-secondary"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  if (pendingPageChange !== null) {
                    clearSelectionBoxAndNavigate(pendingPageChange);
                  }
                }}
                className="ikaro-btn ikaro-btn-sm ikaro-btn-danger"
              >
                Descartar e continuar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentSourcesSidebar;
